import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Icon, TableEmptyRow, TableRowLink, TableSkeletonRows } from "@sagot/ui";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useFormatters } from "../shared/format";
import { ChannelBadge, IdleFlag, LeadAvatar, StageSelect } from "./lead-bits";
import { displayMobile, leadInterest } from "./lead-logic";
import type { Lead, Stage } from "./lead-types";

const COLUMNS = 8;

interface LeadsTableProps {
  id: string;
  leads: readonly Lead[];
  stages: readonly Stage[];
  isPending: boolean;
  canEdit: boolean;
  leadHref: (leadId: string) => string;
  onStageChange: (lead: Lead, stageId: string) => void;
  empty: ReactNode;
}

function LeadRow({ lead, stages, canEdit, leadHref, onStageChange }: Omit<LeadsTableProps, "id" | "leads" | "isPending" | "empty"> & { lead: Lead }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const navigate = useNavigate();
  const { summary } = useWorkspaceContext();
  const href = leadHref(lead.id);
  return (
    <TableRowLink
      data-testid="lead-row"
      onNavigate={() => {
        void navigate(href);
      }}
    >
      <td>
        <div className="flex items-center gap-2.5">
          <LeadAvatar name={lead.name} />
          <span className="min-w-0 max-w-36">
            <span className="flex items-center gap-1.5">
              <AppLink href={href} className="block truncate font-semibold text-fg hover:underline" aria-label={t("engage.leads.openLead", { name: lead.name })}>
                {lead.name}
              </AppLink>
              {lead.idle ? <IdleFlag compact /> : null}
            </span>
            <span className="block truncate text-xs text-fg-subtle" title={lead.email ?? undefined}>
              {lead.email}
            </span>
          </span>
        </div>
      </td>
      <td className="whitespace-nowrap font-mono text-xs" data-testid="lead-mobile">
        {displayMobile(lead.mobile) || "—"}
      </td>
      <td>
        <span className="block max-w-32 truncate" title={leadInterest(lead) || undefined}>
          {leadInterest(lead) || "—"}
        </span>
      </td>
      <td>
        <ChannelBadge channel={lead.source_channel} />
      </td>
      <td>
        {lead.conversation_id ? (
          <AppLink href={consoleRoutes.inbox(summary.slug, lead.conversation_id)} className="inline-flex max-w-32 items-center gap-1 text-primary-ink hover:underline">
            <Icon name="messages-square" className="size-3.5 shrink-0" />
            <span className="truncate">{lead.topic ?? lead.interest ?? t("engage.leads.sheet.openConversation")}</span>
          </AppLink>
        ) : (
          <span className="text-fg-subtle">—</span>
        )}
      </td>
      <td>
        <span className={lead.owner_name ? "block max-w-32 truncate" : "block max-w-32 truncate italic text-fg-subtle"} title={lead.owner_name ?? undefined}>
          {lead.owner_name ?? t("engage.leads.unassigned")}
        </span>
      </td>
      <td>
        <StageSelect
          stages={stages}
          value={lead.stage.id}
          disabled={!canEdit}
          className="h-8 w-32 text-xs"
          label={t("engage.leads.stageLabel", { name: lead.name })}
          onChange={(stageId) => {
            onStageChange(lead, stageId);
          }}
        />
      </td>
      <td className="whitespace-nowrap text-right text-fg-subtle">{format.ago(lead.created_at)}</td>
    </TableRowLink>
  );
}

/** `LeadsTable` from the mockup: row click opens the lead sheet; the name is the keyboard link. */
export function LeadsTable({ id, leads, stages, isPending, canEdit, leadHref, onStageChange, empty }: LeadsTableProps) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto" id={id} role="tabpanel">
      <table className="table [&_td]:px-3 [&_th]:px-3">
        <thead>
          <tr>
            <th>{t("engage.leads.columns.name")}</th>
            <th>{t("engage.leads.columns.mobile")}</th>
            <th>{t("engage.leads.columns.interest")}</th>
            <th>{t("engage.leads.columns.source")}</th>
            <th>{t("engage.leads.columns.conversation")}</th>
            <th>{t("engage.leads.columns.owner")}</th>
            <th>{t("engage.leads.columns.stage")}</th>
            <th className="text-right">{t("engage.leads.columns.created")}</th>
          </tr>
        </thead>
        <tbody>
          {isPending ? (
            <TableSkeletonRows rows={6} columns={COLUMNS} />
          ) : leads.length === 0 ? (
            <TableEmptyRow colSpan={COLUMNS}>{empty}</TableEmptyRow>
          ) : (
            leads.map((lead) => <LeadRow key={lead.id} lead={lead} stages={stages} canEdit={canEdit} leadHref={leadHref} onStageChange={onStageChange} />)
          )}
        </tbody>
      </table>
    </div>
  );
}
